import { blobEndpoint, datasetsContainer } from '~lib/storage/config';

const ABS_API_VERSION = '2024-08-04';

export interface DownloadBlobOptions {
  onProgress?: (bytesDownloaded: number, totalBytes: number | null) => void;
}

// Downloads a single blob via the ABS REST API. Bearer auth means we can't use
// a plain anchor `href` (the browser won't add the token), so we fetch the
// bytes into memory, wrap them in an object URL, and trigger a synthetic
// anchor click to invoke the browser's save dialog.
//
// We pull the response body as a stream rather than calling `.blob()` so we
// can tick a per-chunk progress callback. Memory cost is the same — every
// byte still sits in JS heap until the download completes — but the UI gets
// honest feedback instead of going dark until the very end.
export async function downloadBlob(
  token: string,
  fullPath: string,
  options: DownloadBlobOptions = {},
): Promise<void> {
  const url = `${blobEndpoint}/${datasetsContainer}/${encodeBlobPath(fullPath)}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-ms-version': ABS_API_VERSION,
    },
  });

  if (!response.ok || !response.body) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Download failed: ${response.status} ${response.statusText}${
        body ? ` — ${body.slice(0, 200)}` : ''
      }`,
    );
  }

  const contentLength = response.headers.get('Content-Length');
  const totalBytes = contentLength ? Number(contentLength) : null;

  const reader = response.body.getReader();
  // Typed as BlobPart so the Blob constructor accepts the array without TS
  // worrying that a Uint8Array could be backed by a SharedArrayBuffer.
  const chunks: BlobPart[] = [];
  let bytesDownloaded = 0;

  // Initial tick so the UI moves off 0% immediately, even on slow first
  // chunk.
  options.onProgress?.(0, totalBytes);

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    bytesDownloaded += value.length;
    options.onProgress?.(bytesDownloaded, totalBytes);
  }

  const blob = new Blob(chunks);
  const objectUrl = URL.createObjectURL(blob);
  const filename = fullPath.split('/').pop() ?? 'download';

  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(objectUrl);
}

// Path segments in blob names can contain characters that need URL-encoding
// (spaces, `+`, `#`, etc.), but the `/` separators must stay literal.
function encodeBlobPath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}
